(() => {
  if (window.nhCartRecommendations) return;
  const configuration = document.getElementById('nh-cart-config');
  if (!configuration) return;
  let config;
  try { config = JSON.parse(configuration.textContent); } catch { return; }
  if (!config.sectionUrl || !config.addUrl || !config.cartUrl) return;
  window.nhCartRecommendations = true;

  const labels = config.labels || {};
  const label = (key) => typeof labels[key] === 'string' ? labels[key] : '';
  const jsonCartUrl = new URL(config.cartUrl, window.location.origin);
  if (!jsonCartUrl.pathname.endsWith('.js')) {
    jsonCartUrl.pathname = `${jsonCartUrl.pathname.replace(/\/$/, '')}.js`;
  }
  const exposureKey = 'nh-cart-suggestions-v1';
  let exposure = { quantities: null, pending: true };
  try {
    const saved = JSON.parse(sessionStorage.getItem(exposureKey));
    if (saved && saved.quantities && typeof saved.pending === 'boolean') exposure = saved;
  } catch {}
  const saveExposure = () => {
    try { sessionStorage.setItem(exposureKey, JSON.stringify(exposure)); } catch {}
  };
  let drawerOpen = false;
  let showSuggestions = false;
  let appRoot = null;
  let appObserver = null;
  let discovery = null;
  let mountScheduled = false;
  let cart = null;
  let cartSignature = null;
  let cartRevision = 0;
  let cartRead = null;
  let bootstrapAttempted = false;
  let cachedRoot = null;
  let mountedRoot = null;
  let sectionController = null;
  let sectionRequest = null;
  let sectionVersion = 0;
  let loadedSignature = null;
  let attemptedAt = 0;
  let busy = false;
  let pendingVariant = null;
  let statusText = '';
  let statusError = false;
  const acceptedVariants = new Set();

  const isCart = (value) => value && Array.isArray(value.items)
    && Number.isFinite(Number(value.item_count));

  const signatureOf = (value) => JSON.stringify([
    value.token, value.currency, value.item_count, value.total_price,
    value.items.map((item) => [item.key, item.variant_id || item.id, item.quantity])
  ]);

  const setStatus = (message, error = false) => {
    statusText = message || '';
    statusError = error;
    synchronizeOwnedContent();
  };

  const synchronizeOwnedContent = () => {
    if (!mountedRoot) return;
    mountedRoot.setAttribute('aria-busy', String(busy));
    let status = mountedRoot.querySelector('[data-nh-cart-status]');
    if (!status && statusText) {
      status = document.createElement('p');
      status.dataset.nhCartStatus = '';
      status.className = 'nh-cart-status';
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('role', 'status');
      mountedRoot.append(status);
    }
    if (status) {
      if (status.textContent !== statusText) status.textContent = statusText;
      status.hidden = !statusText;
      status.classList.toggle('is-error', statusError);
    }
    mountedRoot.querySelectorAll('[data-nh-cart-add]').forEach((button) => {
      const id = String(button.dataset.variantId || '');
      const accepted = acceptedVariants.has(id);
      if (!button.dataset.nhOriginalLabel) button.dataset.nhOriginalLabel = button.textContent.trim();
      if (button.dataset.nhOriginalAriaLabel === undefined) {
        button.dataset.nhOriginalAriaLabel = button.getAttribute('aria-label') || '';
      }
      button.disabled = busy || accepted;
      const adding = busy && id === pendingVariant;
      const text = accepted ? '✓' : adding ? '…' : button.dataset.nhOriginalLabel || '+';
      if (button.textContent !== text) button.textContent = text;
      const stateLabel = accepted ? label('added') : adding ? label('adding') : '';
      const originalAriaLabel = button.dataset.nhOriginalAriaLabel;
      const ariaLabel = stateLabel && originalAriaLabel
        ? `${stateLabel} — ${originalAriaLabel}` : stateLabel || originalAriaLabel;
      if (ariaLabel) button.setAttribute('aria-label', ariaLabel);
      else button.removeAttribute('aria-label');
      if (adding) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
      const card = button.closest('[data-nh-cart-card]');
      if (card && accepted) card.hidden = true;
    });
  };

  const unmount = () => {
    mountedRoot?.remove();
    mountedRoot = null;
  };

  const mount = () => {
    mountScheduled = false;
    if (!drawerOpen || !showSuggestions || !appRoot?.isConnected || !cart || Number(cart.item_count) === 0 || !cachedRoot) {
      unmount();
      return;
    }
    const items = appRoot.querySelector('.slidecarthq .items');
    if (!items) return;
    if (!mountedRoot) mountedRoot = cachedRoot.cloneNode(true);
    if (mountedRoot.parentElement !== items || items.lastElementChild !== mountedRoot) {
      items.append(mountedRoot);
    }
    exposure.pending = false;
    saveExposure();
    mountedRoot.setAttribute('role', 'listitem');
    synchronizeOwnedContent();
  };

  const scheduleMount = () => {
    if (mountScheduled) return;
    mountScheduled = true;
    queueMicrotask(mount);
  };

  const attach = () => {
    const nextRoot = document.getElementById('slidecarthq');
    if (!nextRoot) return false;
    if (appRoot !== nextRoot) {
      appObserver?.disconnect();
      appRoot?.removeEventListener('click', onAddClick);
      appRoot = nextRoot;
      appRoot.addEventListener('click', onAddClick);
      appObserver = new MutationObserver((records) => {
        if (records.some((record) => !mountedRoot?.contains(record.target))) scheduleMount();
      });
      appObserver.observe(appRoot, { childList: true, subtree: true });
    }
    discovery?.disconnect();
    discovery = null;
    scheduleMount();
    return true;
  };

  const loadRecommendations = () => {
    if (!cart || Number(cart.item_count) === 0) return Promise.resolve();
    if (loadedSignature === cartSignature || sectionRequest) return sectionRequest || Promise.resolve();
    const signature = cartSignature;
    const version = ++sectionVersion;
    const controller = new AbortController();
    sectionController = controller;
    attemptedAt = Date.now();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    const recommendationUrl = new URL(config.sectionUrl, window.location.origin);
    recommendationUrl.searchParams.set('product_id', String(cart.items[0].product_id));
    sectionRequest = fetch(recommendationUrl.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: controller.signal
    }).then(async (response) => {
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      if (version !== sectionVersion || signature !== cartSignature) return;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const root = documentFragment.querySelector('[data-nh-cart-recommendations]');
      const hasCards = root?.querySelector('[data-nh-cart-card], [data-nh-cart-add]');
      cachedRoot = hasCards ? root : null;
      loadedSignature = signature;
      unmount();
      mount();
    }).catch(() => {
      if (version !== sectionVersion || signature !== cartSignature) return;
      cachedRoot = null;
      unmount();
    }).finally(() => {
      window.clearTimeout(timeout);
      if (version === sectionVersion) {
        sectionRequest = null;
        sectionController = null;
      }
    });
    return sectionRequest;
  };

  const receiveCart = (nextCart, official = false) => {
    if (!isCart(nextCart)) return false;
    const quantities = {};
    nextCart.items.forEach((item) => {
      const id = String(item.variant_id || item.id);
      quantities[id] = (quantities[id] || 0) + Number(item.quantity);
    });
    const added = exposure.quantities && Object.entries(quantities).some(
      ([id, quantity]) => quantity > (Number(exposure.quantities[id]) || 0)
    );
    if (added) {
      exposure.pending = true;
      if (drawerOpen) showSuggestions = true;
    }
    if (Number(nextCart.item_count) === 0) {
      exposure.pending = true;
      showSuggestions = false;
    }
    exposure.quantities = quantities;
    saveExposure();
    cart = nextCart;
    if (official) {
      document.querySelectorAll('[data-header-cart-count]').forEach((element) => {
        const count = String(nextCart.item_count);
        if (element.textContent !== count) element.textContent = count;
      });
    }
    const nextSignature = signatureOf(nextCart);
    if (nextSignature !== cartSignature) {
      cartSignature = nextSignature;
      cartRevision += 1;
      sectionVersion += 1;
      sectionController?.abort();
      sectionController = null;
      sectionRequest = null;
      cachedRoot = null;
      loadedSignature = null;
      unmount();
      if (!busy) {
        const existing = new Set(nextCart.items.map((item) => String(item.variant_id || item.id)));
        acceptedVariants.forEach((id) => {
          if (!existing.has(id)) acceptedVariants.delete(id);
        });
        statusText = '';
        statusError = false;
      }
      if (Number(nextCart.item_count) > 0) loadRecommendations();
    }
    attach();
    return true;
  };

  const readCart = () => {
    if (cartRead) return cartRead;
    const revision = cartRevision;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    cartRead = fetch(jsonCartUrl.href, {
      credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal
    }).then(async (response) => {
      if (!response.ok) throw new Error(String(response.status));
      const value = await response.json();
      if (!isCart(value)) throw new Error('cart');
      if (revision !== cartRevision) return cart || value;
      receiveCart(value);
      return value;
    }).finally(() => {
      window.clearTimeout(timeout);
      cartRead = null;
    });
    return cartRead;
  };

  const synchronizeNativeCart = async () => {
    const revision = cartRevision;
    if (typeof window.SLIDECART_UPDATE === 'function') {
      const updated = await new Promise((resolve) => {
        let settled = false;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          resolve(value);
        };
        const timeout = window.setTimeout(() => finish(false), 6000);
        try {
          window.SLIDECART_UPDATE(function (...args) {
            const value = args.find(isCart);
            if (value) receiveCart(value, true);
            finish(Boolean(value) || cartRevision !== revision);
          });
        } catch { finish(false); }
      });
      if (updated) return;
    }
    if (cartRead) await cartRead.catch(() => {});
    const value = await readCart();
    if (typeof window.SLIDECART_SET_CART === 'function') window.SLIDECART_SET_CART(value);
  };

  async function onAddClick(event) {
    const button = event.target instanceof Element
      ? event.target.closest('[data-nh-cart-recommendations] [data-nh-cart-add]') : null;
    if (!button || !appRoot?.contains(button)) return;
    event.preventDefault();
    if (busy || button.disabled) return;
    const id = Number(button.dataset.variantId);
    if (!Number.isSafeInteger(id) || id <= 0) {
      setStatus(label('error'), true);
      return;
    }
    const variant = String(id);
    if (acceptedVariants.has(variant)) return;
    busy = true;
    pendingVariant = variant;
    setStatus(label('adding'));
    let added = false;
    let failureMessage = '';
    try {
      const response = await fetch(config.addUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] })
      });
      let result;
      try { result = await response.json(); } catch { result = null; }
      if (!response.ok || result?.status >= 400) {
        failureMessage = typeof result?.description === 'string' ? result.description
          : typeof result?.message === 'string' ? result.message : label('error');
        throw new Error(failureMessage);
      }
      added = true;
      acceptedVariants.add(variant);
      setStatus(label('added'));
      await synchronizeNativeCart();
    } catch {
      setStatus(added ? label('added') : failureMessage || label('error'), !added);
    } finally {
      busy = false;
      pendingVariant = null;
      synchronizeOwnedContent();
      if (added && cart && Number(cart.item_count) > 0) loadRecommendations();
    }
  }

  const chain = (name, handler) => {
    const previous = window[name];
    window[name] = function (...args) {
      let result;
      try {
        if (typeof previous === 'function') result = previous.apply(this, args);
      } finally {
        handler.apply(this, args);
      }
      return result;
    };
  };

  chain('SLIDECART_LOADED', function (value) { receiveCart(value, true); });
  chain('SLIDECART_UPDATED', function (value) { receiveCart(value, true); });
  chain('SLIDECART_OPENED', function () {
    drawerOpen = true;
    showSuggestions = showSuggestions || exposure.pending;
    attach();
    readCart().catch(() => {});
    if (!cart) return;
    else if (loadedSignature !== cartSignature && Date.now() - attemptedAt > 10000) loadRecommendations();
    else scheduleMount();
  });

  chain('SLIDECART_CLOSED', function () {
    drawerOpen = false;
    showSuggestions = false;
    unmount();
  });

  const start = () => {
    if (attach()) {
      if (!cart && !bootstrapAttempted) {
        bootstrapAttempted = true;
        readCart().catch(() => {});
      }
      return;
    }
    if (!discovery) {
      discovery = new MutationObserver(() => {
        if (attach() && !cart && !bootstrapAttempted) {
          bootstrapAttempted = true;
          readCart().catch(() => {});
        }
      });
      discovery.observe(document.body, { childList: true, subtree: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
